/* ==========================================================================
   PRC guide v2: header/TOC behaviour (guide-v2.js)
   Preview only, loaded ONLY on article.guide-v2. Independent of guide.js.
   TOC entries are <button data-goto>, never <a href="#id">: no fragment
   href ever exists in the DOM, so no crawler can discover a hashtag URL.
   Idempotent; respects prefers-reduced-motion.
   ========================================================================== */

function slugify(s) { return String(s).toLowerCase().replace(/[^\w]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60); }

function collectHeadings(root) {
  const body = root.querySelector('.gv2__body');
  if (!body) return [];
  const heads = Array.from(body.querySelectorAll('h2'));
  const used = new Set();
  heads.forEach((h, i) => {
    let base = h.id || slugify(h.textContent || '') || ('section-' + (i + 1));
    let id = base, n = 1;
    while (used.has(id)) id = base + '-' + (++n);
    used.add(id);
    h.id = id;
  });
  return heads;
}

function renderList(listEl, heads) {
  listEl.textContent = '';
  heads.forEach((h, i) => {
    const li = document.createElement('li');
    li.className = 'gv2__toc-item';
    const num = document.createElement('span');
    num.className = 'gv2__toc-num';
    num.textContent = String(i + 1).padStart(2, '0');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gv2__toc-link';
    btn.dataset.goto = h.id;
    btn.textContent = h.textContent;
    li.append(num, btn);
    listEl.appendChild(li);
  });
}

function buildToc(root) {
  const heads = collectHeadings(root);
  const desktopList = root.querySelector('[data-gv2-toc-list]');
  const mobileList = root.querySelector('[data-gv2-toc-list-mobile]');
  const toc = root.querySelector('[data-gv2-toc]');
  const mobileToc = root.querySelector('[data-gv2-mobile-toc]');
  const countEl = root.querySelector('[data-gv2-toc-count]');
  if (desktopList) renderList(desktopList, heads);
  if (mobileList) renderList(mobileList, heads);
  const has = heads.length >= 2;
  if (toc) toc.hidden = !has;
  if (mobileToc) mobileToc.hidden = !has;
  if (countEl) countEl.textContent = has ? ('01/' + String(heads.length).padStart(2, '0')) : '';
  return heads;
}

function initSpy(root, heads) {
  if (!heads.length || !('IntersectionObserver' in window)) return;
  if (root.__gv2IO) root.__gv2IO.disconnect();
  const items = () => Array.from(root.querySelectorAll('.gv2__toc-item, .gv2__mobile-toc-panel .gv2__toc-item'));
  let current = null;
  const setActive = (id) => {
    if (id === current) return;
    current = id;
    items().forEach((li) => {
      const btn = li.querySelector('.gv2__toc-link');
      li.classList.toggle('is-active', btn && btn.dataset.goto === id);
    });
  };
  const io = new IntersectionObserver((entries) => {
    const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
    if (visible.length) setActive(visible[0].target.id);
  }, { rootMargin: '-96px 0px -70% 0px', threshold: 0 });
  root.__gv2IO = io;
  heads.forEach((h) => io.observe(h));
  if (heads[0]) setActive(heads[0].id);
}

function initClickScroll(root) {
  if (root.__gv2ClickBound) return;
  root.__gv2ClickBound = true;
  root.addEventListener('click', (e) => {
    const btn = e.target.closest('.gv2__toc-link, .gv2__answer-jump');
    if (!btn) return;
    const target = document.getElementById(btn.dataset.goto);
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    const panel = root.querySelector('[data-gv2-mobile-toc-panel]');
    const trigger = root.querySelector('[data-gv2-mobile-toc]');
    if (panel && !panel.hidden) { panel.hidden = true; trigger && trigger.classList.remove('is-open'); }
  });
}

function initMobileToggle(root) {
  const trigger = root.querySelector('[data-gv2-mobile-toc]');
  const panel = root.querySelector('[data-gv2-mobile-toc-panel]');
  if (!trigger || !panel || trigger.__wired) return;
  trigger.__wired = true;
  const toggle = () => {
    const open = panel.hidden;
    panel.hidden = !open;
    trigger.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', String(open));
  };
  trigger.addEventListener('click', toggle);
  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
  });
}

function initProgress(root) {
  const fill = root.querySelector('[data-gv2-progress-fill]');
  const body = root.querySelector('.gv2__body');
  const topBtn = root.querySelector('[data-gv2-top]');
  if (root.__gv2ScrollBound) return;
  root.__gv2ScrollBound = true;
  const onScroll = () => {
    if (fill && body) {
      const rect = body.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const passed = -rect.top;
      const pct = total > 0 ? Math.min(100, Math.max(0, (passed / total) * 100)) : 0;
      fill.style.width = pct + '%';
    }
    if (topBtn) topBtn.classList.toggle('is-visible', window.scrollY > 700);
  };
  document.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  if (topBtn && !topBtn.__wired) {
    topBtn.__wired = true;
    topBtn.addEventListener('click', () => {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
  }
}

/* --- <guide-accordion [single]>: legacy custom element, still emitted by
   custom.guide_body (FAQ section). Registered here too since guide-v2 never
   loads the original guide.js. --------------------------------------------- */
class GuideAccordion extends HTMLElement {
  connectedCallback() {
    if (this.__wired || !this.hasAttribute('single')) return;
    this.__wired = true;
    this.addEventListener('toggle', (e) => {
      const d = e.target;
      if (d.parentElement === this && d.open) {
        this.querySelectorAll(':scope > details[open]').forEach((o) => { if (o !== d) o.open = false; });
      }
    }, true);
  }
}
if (!customElements.get('guide-accordion')) customElements.define('guide-accordion', GuideAccordion);

function initGuideV2(root) {
  const heads = buildToc(root);
  initSpy(root, heads);
  initClickScroll(root);
  initMobileToggle(root);
  initProgress(root);
}

const boot = () => { document.querySelectorAll('.gv2').forEach(initGuideV2); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', boot);
